import React, { createContext, useContext, useState, useEffect } from 'react';

// Branding configuration interface
export interface BrandingConfig {
  // Company Information
  companyName: string;
  companyLogo?: string;
  companyLogoUrl?: string;
  tagline?: string;
  
  // Color Scheme
  colors: {
    primary: string;
    secondary: string;
    accent: string;
    background: string;
    surface: string;
    text: {
      primary: string;
      secondary: string;
      muted: string;
    };
    success: string;
    warning: string;
    error: string;
  };
  
  // Typography
  fonts: {
    primary: string;
    secondary: string;
    headings: string;
  };
  
  // Layout & Styling
  borderRadius: 'none' | 'small' | 'medium' | 'large' | 'full';
  shadows: boolean;
  animations: boolean;
  
  // Contact Information
  contact?: {
    phone?: string;
    email?: string;
    address?: string;
    website?: string;
    socialMedia?: {
      facebook?: string;
      twitter?: string;
      linkedin?: string;
      instagram?: string;
    };
  };
  
  // Legal/Professional Information
  professional?: {
    licenseNumber?: string;
    barAdmission?: string;
    specialties?: string[];
    yearsOfExperience?: number;
    certifications?: string[];
  };
  
  // Custom CSS Overrides
  customCss?: string;
}

// Default branding configuration
const defaultBranding: BrandingConfig = {
  companyName: 'Customer Connect Video',
  companyLogo: '/cc-logo.png',
  tagline: 'Professional video consultation platform',
  colors: {
    primary: '#2563eb', // blue-600
    secondary: '#64748b', // slate-500
    accent: '#0ea5e9', // sky-500
    background: '#f8fafc', // slate-50
    surface: '#ffffff',
    text: {
      primary: '#0f172a', // slate-900
      secondary: '#475569', // slate-600
      muted: '#94a3b8', // slate-400
    },
    success: '#10b981', // emerald-500
    warning: '#f59e0b', // amber-500
    error: '#ef4444', // red-500
  },
  fonts: {
    primary: 'Inter, system-ui, sans-serif',
    secondary: 'Inter, system-ui, sans-serif',
    headings: 'Inter, system-ui, sans-serif',
  },
  borderRadius: 'medium',
  shadows: true,
  animations: true,
};

// Legal-specific default branding
export const legalBranding: BrandingConfig = {
  ...defaultBranding,
  companyName: 'Smith & Associates Law Firm',
  tagline: 'Trusted Legal Counsel Since 1995',
  colors: {
    ...defaultBranding.colors,
    primary: '#1e40af', // blue-800 (more professional)
    secondary: '#374151', // gray-700
    accent: '#3b82f6', // blue-500
  },
  professional: {
    barAdmission: 'State Bar of California',
    specialties: ['Personal Injury', 'Criminal Defense', 'Family Law'],
    yearsOfExperience: 25,
  },
};

// Healthcare-specific default branding
export const healthcareBranding: BrandingConfig = {
  ...defaultBranding,
  companyName: 'MedCare Telehealth',
  tagline: 'Quality Healthcare, Accessible Anywhere',
  colors: {
    ...defaultBranding.colors,
    primary: '#059669', // emerald-600
    secondary: '#6b7280', // gray-500
    accent: '#10b981', // emerald-500
  },
};

// Business/Corporate default branding
export const businessBranding: BrandingConfig = {
  ...defaultBranding,
  companyName: 'Customer Connect Video',
  companyLogo: '/cc-logo.png',
  tagline: 'Professional video consultation platform',
  colors: {
    ...defaultBranding.colors,
    primary: '#4f46e5', // indigo-600
    secondary: '#6b7280', // gray-500
    accent: '#8b5cf6', // violet-500
  },
};

interface BrandingContextType {
  branding: BrandingConfig;
  setBranding: (branding: Partial<BrandingConfig>) => void;
  resetBranding: () => void;
  applyPreset: (preset: 'legal' | 'healthcare' | 'business' | 'default') => void;
  getCSSVariables: () => React.CSSProperties;
  saveBranding: () => Promise<void>;
  loadBranding: () => Promise<void>;
  isLoading: boolean;
  isInitialLoading: boolean;
  hasCustomBranding: boolean;
  lastSaved: string | null;
}

const BrandingContext = createContext<BrandingContextType | undefined>(undefined);

export const useBranding = () => {
  const context = useContext(BrandingContext);
  if (!context) {
    throw new Error('useBranding must be used within a BrandingProvider');
  }
  return context;
};

interface BrandingProviderProps {
  children: React.ReactNode;
  initialBranding?: Partial<BrandingConfig>;
  templateType?: 'legal' | 'healthcare' | 'business' | 'general' | 'default';
}

export const BrandingProvider: React.FC<BrandingProviderProps> = ({
  children,
  initialBranding,
  templateType = 'default'
}) => {
  // Get initial branding based on template type
  const getInitialBranding = () => {
    let baseBranding = defaultBranding;
    
    switch (templateType) {
      case 'legal':
        baseBranding = legalBranding;
        break;
      case 'healthcare':
        baseBranding = healthcareBranding;
        break;
      case 'business':
        baseBranding = businessBranding;
        break;
      case 'general':
        baseBranding = defaultBranding;
        break;
      default:
        baseBranding = defaultBranding;
    }
    
    return initialBranding ? { ...baseBranding, ...initialBranding } : baseBranding;
  };

  const [branding, setBrandingState] = useState<BrandingConfig>(getInitialBranding);
  const [isLoading, setIsLoading] = useState(false);
  const [isInitialLoading, setIsInitialLoading] = useState(true); // Track initial load to prevent FOUC
  const [hasCustomBranding, setHasCustomBranding] = useState(false);
  const [lastSaved, setLastSaved] = useState<string | null>(null);

  // Apply branding from URL parameters on mount
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const brandingOverrides: Partial<BrandingConfig> = {};

    // Extract branding parameters from URL
    if (urlParams.get('companyName')) {
      brandingOverrides.companyName = urlParams.get('companyName')!;
    }
    if (urlParams.get('primaryColor')) {
      brandingOverrides.colors = {
        ...branding.colors,
        primary: urlParams.get('primaryColor')!,
      };
    }
    if (urlParams.get('logo')) {
      brandingOverrides.companyLogo = urlParams.get('logo')!;
    }

    if (Object.keys(brandingOverrides).length > 0) {
      setBrandingState(prev => ({ ...prev, ...brandingOverrides }));
    }
  }, []);

  const setBranding = (updates: Partial<BrandingConfig>) => {
    setBrandingState(prev => ({
      ...prev,
      ...updates,
      colors: updates.colors ? { ...prev.colors, ...updates.colors } : prev.colors,
      fonts: updates.fonts ? { ...prev.fonts, ...updates.fonts } : prev.fonts,
      contact: updates.contact ? { ...prev.contact, ...updates.contact } : prev.contact,
      professional: updates.professional ? { ...prev.professional, ...updates.professional } : prev.professional,
    }));
  };

  const resetBranding = () => {
    setBrandingState(getInitialBranding());
  };

  const applyPreset = (preset: 'legal' | 'healthcare' | 'business' | 'default') => {
    switch (preset) {
      case 'legal':
        setBrandingState(legalBranding);
        break;
      case 'healthcare':
        setBrandingState(healthcareBranding);
        break;
      case 'business':
        setBrandingState(businessBranding);
        break;
      default:
        setBrandingState(defaultBranding);
    }
  };

  // Save branding to API
  const saveBranding = async () => {
    setIsLoading(true);
    try {
      // Get organization ID from URL or use demo org
      const pathname = window.location.pathname;
      const orgMatch = pathname.match(/\/(org_[a-zA-Z0-9_]+)\//);
      const orgId = orgMatch ? orgMatch[1] : 'org_1754194082893_o02h6k'; // Use URL org or fallback to demo
      
      console.log('[BrandingContext] Saving branding for org:', orgId, 'template:', templateType);
      
      const response = await fetch(`https://dev-platform-api-dev.benjiemalinao879557.workers.dev/api/v1/organizations/${orgId}/branding/${templateType}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ branding })
      });

      if (!response.ok) {
        throw new Error(`Failed to save branding: ${response.status}`);
      }

      const result = await response.json();
      setLastSaved(result.data.lastUpdated);
      setHasCustomBranding(true);
      console.log('[BrandingContext] Branding saved successfully');
    } catch (error) {
      console.error('[BrandingContext] Error saving branding:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  // Load branding from API
  const loadBranding = async () => {
    setIsLoading(true);
    try {
      // Get organization ID from URL or use demo org
      const pathname = window.location.pathname;
      const orgMatch = pathname.match(/\/(org_[a-zA-Z0-9_]+)\//);
      const orgId = orgMatch ? orgMatch[1] : 'org_1754194082893_o02h6k'; // Use URL org or fallback to demo
      
      console.log('[BrandingContext] Loading branding for org:', orgId, 'template:', templateType);
      
      const response = await fetch(`https://dev-platform-api-dev.benjiemalinao879557.workers.dev/api/v1/organizations/${orgId}/branding/${templateType}`);

      if (!response.ok) {
        throw new Error(`Failed to load branding: ${response.status}`);
      }

      const result = await response.json();
      
      if (result.success && result.data.branding) {
        setBrandingState(result.data.branding);
        setHasCustomBranding(result.data.hasCustomBranding);
        setLastSaved(result.data.lastUpdated);
        console.log('[BrandingContext] Branding loaded successfully');
      }
    } catch (error) {
      console.error('[BrandingContext] Error loading branding:', error);
      // Don't throw - just use defaults
      setHasCustomBranding(false);
    } finally {
      setIsLoading(false);
      setIsInitialLoading(false); // Mark initial loading as complete
    }
  };

  // Load branding on mount (after URL params are applied)
  useEffect(() => {
    // Only load from API if no URL overrides are present
    const urlParams = new URLSearchParams(window.location.search);
    const hasUrlOverrides = urlParams.get('companyName') || urlParams.get('primaryColor') || urlParams.get('logo');
    
    // Load branding from API if no URL overrides are present
    if (!hasUrlOverrides) {
      loadBranding();
    } else {
      // If URL overrides are present, skip API loading and mark initial loading as complete
      setIsInitialLoading(false);
    }
  }, [templateType]);

  // Generate CSS custom properties for theming
  const getCSSVariables = (): React.CSSProperties => {
    const borderRadiusMap = {
      none: '0px',
      small: '4px',
      medium: '8px',
      large: '12px',
      full: '9999px',
    };

    return {
      '--brand-primary': branding.colors.primary,
      '--brand-secondary': branding.colors.secondary,
      '--brand-accent': branding.colors.accent,
      '--brand-background': branding.colors.background,
      '--brand-surface': branding.colors.surface,
      '--brand-text-primary': branding.colors.text.primary,
      '--brand-text-secondary': branding.colors.text.secondary,
      '--brand-text-muted': branding.colors.text.muted,
      '--brand-success': branding.colors.success,
      '--brand-warning': branding.colors.warning,
      '--brand-error': branding.colors.error,
      '--brand-font-primary': branding.fonts.primary,
      '--brand-font-secondary': branding.fonts.secondary,
      '--brand-font-headings': branding.fonts.headings,
      '--brand-border-radius': borderRadiusMap[branding.borderRadius],
      '--brand-shadow': branding.shadows ? '0 4px 6px -1px rgb(0 0 0 / 0.1)' : 'none',
    } as React.CSSProperties;
  };

  return (
    <BrandingContext.Provider
      value={{
        branding,
        setBranding,
        resetBranding,
        applyPreset,
        getCSSVariables,
        saveBranding,
        loadBranding,
        isLoading,
        isInitialLoading,
        hasCustomBranding,
        lastSaved,
      }}
    >
      <div style={getCSSVariables()}>
        {branding.customCss && (
          <style dangerouslySetInnerHTML={{ __html: branding.customCss }} />
        )}
        {isInitialLoading ? (
          // Show loading state to prevent FOUC
          <div style={{
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: '#f8fafc'
          }}>
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '12px'
            }}>
              <div style={{
                width: '40px',
                height: '40px',
                border: '4px solid #e2e8f0',
                borderTop: '4px solid #2563eb',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite'
              }}></div>
              <p style={{
                color: '#64748b',
                fontSize: '14px',
                margin: '0'
              }}>Loading branding...</p>
            </div>
            <style>{`
              @keyframes spin {
                0% { transform: rotate(0deg); }
                100% { transform: rotate(360deg); }
              }
            `}</style>
          </div>
        ) : (
          children
        )}
      </div>
    </BrandingContext.Provider>
  );
};